import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ComposeSuitePickEmpty,
  ComposeSuitePickModal,
  ComposeSuitePickRow,
} from "@/features/compose/ComposeSuitePickModal";
import {
  fetchCoffreFileBlob,
  fetchCoffreListCached,
  prefetchCoffreList,
  type CoffreEntry,
  type CoffreListZone,
} from "@/lib/suite/coffreSuiteApi";
import { getSuiteAppUrl } from "@/lib/suite/suiteApiFetch";
import { MAIL_COFFRE_UNLOCKED_EVENT, requestCoffreUnlock } from "@/lib/suite/coffreUnlock";
import { useCoffreSuiteStatus } from "@/hooks/useCoffreSuiteStatus";
import { formatComposeAttachmentSize } from "@/lib/compose/composeAttachments";
import { cn } from "@/lib/cn";

type PickItem = {
  path: string;
  name: string;
  size: number;
  zone: CoffreListZone;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (files: File[]) => void | Promise<void>;
  overlayBackdrop?: "default" | "transparent" | "stacked";
};

const ZONES: { id: CoffreListZone; label: string }[] = [
  { id: "vault", label: "Coffre" },
  { id: "ged", label: "GED" },
];

function entryLabel(entry: CoffreEntry): string {
  return entry.name || entry.path.split("/").filter(Boolean).pop() || entry.path;
}

export function CoffreAttachPickerModal({
  open,
  onClose,
  onConfirm,
  overlayBackdrop,
}: Props) {
  const { status: coffreStatus, refresh: refreshCoffreStatus } = useCoffreSuiteStatus();
  const coffreStatusRef = useRef(coffreStatus);
  coffreStatusRef.current = coffreStatus;
  const abortRef = useRef<AbortController | null>(null);
  const [zone, setZone] = useState<CoffreListZone>("vault");
  const [currentPath, setCurrentPath] = useState("");
  const [entries, setEntries] = useState<CoffreEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Map<string, PickItem>>(() => new Map());
  const [locked, setLocked] = useState(false);
  const [needsVault, setNeedsVault] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const applyLockedState = useCallback((message?: string) => {
    setNeedsVault(false);
    setLocked(true);
    setEntries([]);
    setStatusMessage(
      message ?? "Le Coffre est fermé. Saisissez votre mot de passe maître pour joindre un fichier."
    );
  }, []);

  const loadFolder = useCallback(
    async (
      path: string,
      listZone: CoffreListZone,
      options: { refreshStatus?: boolean; background?: boolean } = {}
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!options.background) setLoading(true);
      setStatusMessage(null);
      try {
        const status =
          options.refreshStatus || !coffreStatusRef.current
            ? await refreshCoffreStatus()
            : coffreStatusRef.current;
        if (controller.signal.aborted) return;
        if (!status?.ok) {
          setStatusMessage(status?.message ?? "Coffre indisponible.");
          return;
        }
        if (!status.unlocked) {
          if (!status.hasVault) {
            setNeedsVault(true);
            setLocked(true);
            setEntries([]);
            setStatusMessage(status.message ?? "Activez d’abord votre Coffre.");
            return;
          }
          applyLockedState(status.message);
          requestCoffreUnlock();
          return;
        }
        setLocked(false);
        const result = await fetchCoffreListCached(path, listZone, controller.signal);
        if (controller.signal.aborted) return;
        if (result.error) {
          if (result.error.toLowerCase().includes("déverrouill")) {
            applyLockedState(result.error);
            requestCoffreUnlock();
            void refreshCoffreStatus();
          } else {
            setStatusMessage(result.error);
            if (!options.background) setEntries([]);
          }
          return;
        }
        setCurrentPath(result.path);
        setEntries(result.entries);
      } catch (err) {
        if (controller.signal.aborted) return;
        setStatusMessage(
          err instanceof Error ? err.message : "Impossible de lire le Coffre."
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [applyLockedState, refreshCoffreStatus]
  );

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      return;
    }
    setSearch("");
    setSelected(new Map());
    setZone("vault");
    setCurrentPath("");
    setLocked(false);
    setNeedsVault(false);
    setStatusMessage(null);
    prefetchCoffreList("", "vault");
    void loadFolder("", "vault", { refreshStatus: true });
    // Réinit uniquement à l’ouverture — pas au refresh statut Coffre (évite le rebond racine).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFolder volontairement exclu
  }, [open]);

  useEffect(() => {
    const onUnlock = () => {
      if (!open) return;
      void loadFolder(currentPath, zone, { refreshStatus: true });
    };
    window.addEventListener(MAIL_COFFRE_UNLOCKED_EVENT, onUnlock);
    return () => window.removeEventListener(MAIL_COFFRE_UNLOCKED_EVENT, onUnlock);
  }, [open, currentPath, zone, loadFolder]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const crumbs = currentPath ? currentPath.split("/").filter(Boolean) : [];
  const q = search.trim().toLowerCase();

  const folders = useMemo(
    () =>
      entries.filter(
        (entry) =>
          entry.kind === "folder" &&
          (!q || entryLabel(entry).toLowerCase().includes(q))
      ),
    [entries, q]
  );

  const files = useMemo(
    () =>
      entries.filter(
        (entry) =>
          entry.kind === "file" &&
          (!q || entryLabel(entry).toLowerCase().includes(q))
      ),
    [entries, q]
  );

  function navigateTo(path: string) {
    void loadFolder(path, zone, { background: entries.length > 0 });
  }

  function onCrumbClick(index: number) {
    if (index < 0) {
      navigateTo("");
      return;
    }
    navigateTo(crumbs.slice(0, index + 1).join("/"));
  }

  function toggleFile(entry: CoffreEntry) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(entry.path)) next.delete(entry.path);
      else {
        next.set(entry.path, {
          path: entry.path,
          name: entryLabel(entry),
          size: entry.size ?? 0,
          zone,
        });
      }
      return next;
    });
  }

  async function handleConfirm() {
    const items = [...selected.values()];
    if (items.length === 0 || busy) return;

    setBusy(true);
    try {
      const filesOut: File[] = [];
      for (const item of items) {
        const { blob, filename, error } = await fetchCoffreFileBlob(item.path, {
          zone: item.zone,
        });
        if (!blob || error) {
          setStatusMessage(error ?? `Impossible de charger ${item.name}.`);
          return;
        }
        filesOut.push(
          new File([blob], filename || item.name, {
            type: blob.type || "application/octet-stream",
          })
        );
      }
      await onConfirm(filesOut);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const selectedCount = selected.size;
  const empty = !loading && !locked && folders.length === 0 && files.length === 0;

  return (
    <ComposeSuitePickModal
      open={open}
      title="Joindre depuis Coffre"
      subtitle={zone === "ged" ? "Espace GED" : "Espace sécurisé"}
      overlayBackdrop={overlayBackdrop}
      busy={busy}
      busyLabel="Préparation des fichiers…"
      loading={loading}
      search={search}
      onSearchChange={setSearch}
      crumbs={crumbs}
      onCrumbClick={onCrumbClick}
      onClose={onClose}
      footer={
        <div className="compose-suite-pick-modal__footer">
          {statusMessage ? (
            <p className="compose-suite-pick-modal__error" role="alert">
              {statusMessage}
              {locked && !needsVault ? (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => requestCoffreUnlock()}
                    className="text-[var(--mail-accent)] underline"
                  >
                    Saisir le mot de passe maître
                  </button>
                </>
              ) : null}
              {needsVault ? (
                <>
                  {" "}
                  <a
                    href={getSuiteAppUrl("coffre/dist/index.html")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--mail-accent)] underline"
                  >
                    Ouvrir le Coffre
                  </a>
                </>
              ) : null}
            </p>
          ) : (
            <p className="compose-suite-pick-modal__hint">
              {selectedCount > 0
                ? `${selectedCount} fichier${selectedCount > 1 ? "s" : ""} sélectionné${selectedCount > 1 ? "s" : ""}`
                : "Sélectionnez un ou plusieurs fichiers"}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="compose-suite-pick-modal__btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Annuler
            </button>
            <button
              type="button"
              className="compose-suite-pick-modal__btn-primary mail-btn-primary"
              onClick={() => void handleConfirm()}
              disabled={busy || selectedCount === 0 || locked}
            >
              Joindre
            </button>
          </div>
        </div>
      }
    >
      <div className="compose-suite-pick-modal__tabs" role="tablist">
        {ZONES.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={zone === tab.id}
            className={cn(
              "compose-suite-pick-modal__tab",
              zone === tab.id && "compose-suite-pick-modal__tab--active"
            )}
            onClick={() => {
              if (tab.id === zone) return;
              setZone(tab.id);
              setSelected(new Map());
              setCurrentPath("");
              prefetchCoffreList("", tab.id);
              void loadFolder("", tab.id);
            }}
            disabled={busy}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {empty ? <ComposeSuitePickEmpty label="Dossier vide" /> : null}

      {folders.map((entry) => (
        <ComposeSuitePickRow
          key={`folder-${entry.path}`}
          kind="folder"
          label={entryLabel(entry)}
          onClick={() => navigateTo(entry.path)}
          disabled={busy}
        />
      ))}

      {files.map((entry) => (
        <ComposeSuitePickRow
          key={`file-${entry.path}`}
          kind="file"
          label={entryLabel(entry)}
          meta={entry.size ? formatComposeAttachmentSize(entry.size) : undefined}
          selected={selected.has(entry.path)}
          onClick={() => toggleFile(entry)}
          disabled={busy}
        />
      ))}
    </ComposeSuitePickModal>
  );
}
